import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ThemeSwitcher } from "../components/ThemeSwitcher";
import {
  DEFAULT_MEMBER_TABS,
  MEMBER_OFFER_TAB_IDS,
  normalizeOfferAccess,
  type MemberOfferTabId,
  type OfferAccessEntry,
} from "../lib/offerAccess";

interface DashboardUser {
  _id: string;
  email: string;
  name: string;
  role: "admin" | "agency_owner" | "member";
  permissions: string[];
  offerAccess: OfferAccessEntry[];
  status: "active" | "pending";
  avatarColor: string;
  createdAt: string;
}

interface Invite {
  _id: string;
  email: string;
  role?: DashboardUser["role"];
  permissions: string[];
  offerAccess: OfferAccessEntry[];
  invitedBy: string;
  createdAt: string;
}

const AVAILABLE_TABS: Array<{ id: MemberOfferTabId; label: string; category: string | null }> = [
  { id: "dashboard", label: "Dashboard", category: null },
  { id: "leaderboard", label: "Leaderboard", category: null },
  { id: "daily-report", label: "Daily Report", category: "Sales Analytics" },
  { id: "acquisition", label: "Acquisition", category: "Sales Analytics" },
  { id: "closers", label: "Closers", category: "Sales Analytics" },
  { id: "setters", label: "Setters", category: "Sales Analytics" },
  { id: "content", label: "Content", category: "Sales Analytics" },
  { id: "leads", label: "Leads", category: "Pipeline" },
  { id: "utm-builder", label: "UTM Builder", category: "Tools" },
  { id: "eod-builder", label: "EOD Builder", category: "Tools" },
  { id: "rev-projector", label: "Rev Projector", category: "Tools" },
];

const ROLE_OPTIONS: DashboardUser["role"][] = ["member", "agency_owner", "admin"];

const AVAILABLE_PERMISSIONS = [
  "dashboard",
  "leaderboard",
  "daily-report",
  "acquisition",
  "closers",
  "setters",
  "content",
  "leads",
  "utm-builder",
  "eod-builder",
  "rev-projector",
  "financials",
  "admin",
] as const;

function PermissionChip({ label }: { label: string }) {
  return (
    <span className="rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground">
      {label}
    </span>
  );
}

function Checkbox({
  checked,
  onChange,
  label,
  disabled = false,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <label className={`flex items-center gap-2 text-sm ${disabled ? "opacity-50" : ""}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className="h-4 w-4 rounded border-border bg-background"
      />
      <span>{label}</span>
    </label>
  );
}

function PermissionEditor({
  value,
  onChange,
}: {
  value: string[];
  onChange: (value: string[]) => void;
}) {
  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
      {AVAILABLE_PERMISSIONS.map((permission) => {
        const checked = value.includes(permission);
        return (
          <Checkbox
            key={permission}
            checked={checked}
            label={permission}
            onChange={() =>
              onChange(
                checked
                  ? value.filter((item) => item !== permission)
                  : [...value, permission],
              )
            }
          />
        );
      })}
    </div>
  );
}

function OfferAccessEditor({
  offers,
  value,
  onChange,
}: {
  offers: string[];
  value: OfferAccessEntry[];
  onChange: (value: OfferAccessEntry[]) => void;
}) {
  const addRow = () => {
    const nextOffer = offers.find((offer) => !value.some((entry) => entry.offer === offer)) || "";
    if (!nextOffer) return;
    onChange([...value, { offer: nextOffer, tabs: [...DEFAULT_MEMBER_TABS] }]);
  };

  const updateEntry = (index: number, nextEntry: OfferAccessEntry) => {
    const nextValue = value.slice();
    nextValue[index] = nextEntry;
    onChange(nextValue);
  };

  const removeEntry = (index: number) => {
    onChange(value.filter((_, entryIndex) => entryIndex !== index));
  };

  return (
    <div className="space-y-4">
      {value.map((entry, index) => {
        const usedOffers = new Set(value.map((item) => item.offer));
        return (
          <div key={`${entry.offer}-${index}`} className="rounded-xl border border-border bg-muted/20 p-4">
            <div className="mb-4 flex items-center gap-3">
              <select
                value={entry.offer}
                onChange={(event) =>
                  updateEntry(index, {
                    ...entry,
                    offer: event.target.value,
                  })
                }
                className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
              >
                {offers.map((offer) => {
                  const disabled = offer !== entry.offer && usedOffers.has(offer);
                  return (
                    <option key={offer} value={offer} disabled={disabled}>
                      {offer}
                    </option>
                  );
                })}
              </select>
              <button
                type="button"
                onClick={() => removeEntry(index)}
                className="rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
              >
                Remove
              </button>
            </div>

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {AVAILABLE_TABS.map((tab) => {
                const checked = entry.tabs.includes(tab.id);
                const disabled = tab.id === "dashboard";
                return (
                  <Checkbox
                    key={tab.id}
                    checked={checked}
                    disabled={disabled}
                    label={tab.label}
                    onChange={() => {
                      const nextTabs = checked
                        ? entry.tabs.filter((item) => item !== tab.id)
                        : [...entry.tabs, tab.id];
                      updateEntry(index, {
                        ...entry,
                        tabs: checked && tab.id === "dashboard" ? entry.tabs : nextTabs,
                      });
                    }}
                  />
                );
              })}
            </div>
          </div>
        );
      })}

      <button
        type="button"
        onClick={addRow}
        disabled={value.length >= offers.length}
        className="rounded-xl border border-dashed border-border px-4 py-3 text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
      >
        Add Offer Access
      </button>
    </div>
  );
}

function InvitePanel({
  isOpen,
  onClose,
  offers,
  onInvite,
}: {
  isOpen: boolean;
  onClose: () => void;
  offers: string[];
  onInvite: (payload: {
    email: string;
    name: string;
    role: DashboardUser["role"];
    permissions: string[];
    offerAccess: OfferAccessEntry[];
  }) => Promise<void>;
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<DashboardUser["role"]>("member");
  const [permissions, setPermissions] = useState<string[]>([...AVAILABLE_PERMISSIONS]);
  const [offerAccess, setOfferAccess] = useState<OfferAccessEntry[]>(offers[0] ? [{ offer: offers[0], tabs: [...DEFAULT_MEMBER_TABS] }] : []);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setEmail("");
      setName("");
      setRole("member");
      setPermissions([...AVAILABLE_PERMISSIONS]);
      setOfferAccess(offers[0] ? [{ offer: offers[0], tabs: [...DEFAULT_MEMBER_TABS] }] : []);
      setError(null);
    }
  }, [isOpen, offers]);

  if (!isOpen) return null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      setSubmitting(true);
      setError(null);
      await onInvite({
        email,
        name,
        role,
        permissions,
        offerAccess: role === "member" ? offerAccess : [],
      });
      onClose();
    } catch (inviteError) {
      setError(inviteError instanceof Error ? inviteError.message : "Failed to send invite");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />
      <div className="fixed right-0 top-0 z-50 h-full w-full max-w-2xl overflow-y-auto border-l border-border bg-card p-6 shadow-2xl">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold">Invite Team Member</h3>
            <p className="text-sm text-muted-foreground">Assign role, offers, and per-offer tabs.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg border border-border px-3 py-2 text-sm">
            Close
          </button>
        </div>

        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="grid gap-4 md:grid-cols-2">
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Full name"
              className="rounded-xl border border-border bg-background px-4 py-3 text-sm"
              required
            />
            <input
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="Email"
              type="email"
              className="rounded-xl border border-border bg-background px-4 py-3 text-sm"
              required
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">Role</label>
            <select
              value={role}
              onChange={(event) => setRole(event.target.value as DashboardUser["role"])}
              className="rounded-xl border border-border bg-background px-4 py-3 text-sm"
            >
              {ROLE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {role === "member" && (
            <div>
              <h4 className="mb-3 text-sm font-medium">Offer Access</h4>
              <OfferAccessEditor offers={offers} value={offerAccess} onChange={setOfferAccess} />
            </div>
          )}

          {role === "agency_owner" && (
            <div>
              <h4 className="mb-3 text-sm font-medium">Agency Owner Tab Access</h4>
              <PermissionEditor value={permissions} onChange={setPermissions} />
            </div>
          )}

          {error && <p className="text-sm text-red-500">{error}</p>}

          <div className="flex items-center justify-end gap-3">
            <button type="button" onClick={onClose} className="rounded-xl border border-border px-4 py-2.5 text-sm">
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-xl bg-[var(--brand-primary)] px-4 py-2.5 text-sm font-medium text-white disabled:opacity-50"
            >
              {submitting ? "Sending..." : "Send Invite"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

function UserRow({
  user,
  offers,
  onSave,
  onDelete,
}: {
  user: DashboardUser;
  offers: string[];
  onSave: (userId: string, payload: Partial<DashboardUser>) => Promise<void>;
  onDelete: (userId: string) => Promise<void>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [draftRole, setDraftRole] = useState(user.role);
  const [draftPermissions, setDraftPermissions] = useState<string[]>(user.permissions || []);
  const [draftOfferAccess, setDraftOfferAccess] = useState<OfferAccessEntry[]>(normalizeOfferAccess(user.offerAccess));
  const isMember = draftRole === "member";
  const isAgencyOwner = draftRole === "agency_owner";

  useEffect(() => {
    setDraftRole(user.role);
    setDraftPermissions(user.permissions || []);
    setDraftOfferAccess(normalizeOfferAccess(user.offerAccess));
  }, [user.offerAccess, user.permissions, user.role]);

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div className="space-y-1">
          <h3 className="text-base font-semibold">{user.name}</h3>
          <p className="text-sm text-muted-foreground">{user.email}</p>
          <div className="flex flex-wrap gap-2 pt-2">
            <PermissionChip label={user.role} />
            <PermissionChip label={user.status} />
            {user.role === "member" && (
              <PermissionChip label={`${user.offerAccess.length} assigned offer${user.offerAccess.length === 1 ? "" : "s"}`} />
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {user.role === "member" && user.offerAccess.length > 0 && user.offerAccess.map((entry) => (
            <PermissionChip key={entry.offer} label={entry.offer} />
          ))}
          <button
            type="button"
            onClick={() => setExpanded((current) => !current)}
            className="rounded-xl border border-border px-4 py-2 text-sm"
          >
            {expanded ? "Close" : "Edit Access"}
          </button>
          <button
            type="button"
            onClick={() => void onDelete(user._id)}
            className="rounded-xl border border-red-500/30 px-4 py-2 text-sm text-red-500"
          >
            Remove
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-5 space-y-5 border-t border-border pt-5">
          <div>
            <label className="mb-2 block text-sm font-medium">Role</label>
            <select
              value={draftRole}
              onChange={(event) => setDraftRole(event.target.value as DashboardUser["role"])}
              className="rounded-xl border border-border bg-background px-4 py-3 text-sm"
            >
              {ROLE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {isMember ? (
            <div>
              <h4 className="mb-3 text-sm font-medium">Offer Access</h4>
              <OfferAccessEditor offers={offers} value={draftOfferAccess} onChange={setDraftOfferAccess} />
            </div>
          ) : isAgencyOwner ? (
            <div>
              <h4 className="mb-3 text-sm font-medium">Agency Owner Tab Access</h4>
              <PermissionEditor value={draftPermissions} onChange={setDraftPermissions} />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Elevated roles keep full access across agency and offer views.</p>
          )}

          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={() => void onSave(user._id, {
                role: draftRole,
                permissions: draftRole === "agency_owner" ? draftPermissions : user.permissions,
                offerAccess: draftRole === "member" ? draftOfferAccess : [],
              })}
              className="rounded-xl bg-[var(--brand-primary)] px-4 py-2.5 text-sm font-medium text-white"
            >
              Save Changes
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function TeamTab() {
  const [users, setUsers] = useState<DashboardUser[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [offers, setOffers] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [invitePanelOpen, setInvitePanelOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersRes, invitesRes, offersRes] = await Promise.all([
        fetch("/api/users"),
        fetch("/api/invites"),
        fetch("/api/offers"),
      ]);

      const [usersJson, invitesJson, offersJson] = await Promise.all([
        usersRes.json(),
        invitesRes.json(),
        offersRes.json(),
      ]);

      if (usersJson.success) {
        setUsers(usersJson.data.map((user: DashboardUser) => ({
          ...user,
          offerAccess: normalizeOfferAccess(user.offerAccess),
        })));
      }

      if (invitesJson.success) {
        setInvites(invitesJson.data.map((invite: Invite) => ({
          ...invite,
          offerAccess: normalizeOfferAccess(invite.offerAccess),
        })));
      }

      if (offersJson.success) {
        setOffers(offersJson.data.map((offer: { name: string }) => offer.name));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const handleInvite = async (payload: {
    email: string;
    name: string;
    role: DashboardUser["role"];
    permissions: string[];
    offerAccess: OfferAccessEntry[];
  }) => {
    const response = await fetch("/api/users/invite", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || "Failed to send invite");
    }
    await fetchData();
  };

  const handleSaveUser = async (userId: string, payload: Partial<DashboardUser>) => {
    const response = await fetch(`/api/users/${userId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || "Failed to update user");
    }
    await fetchData();
  };

  const handleDeleteUser = async (userId: string) => {
    const response = await fetch(`/api/users/${userId}`, { method: "DELETE" });
    const result = await response.json();
    if (result.success) {
      await fetchData();
    }
  };

  const memberCount = useMemo(() => users.filter((user) => user.role === "member").length, [users]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--brand-primary)]/20 border-t-[var(--brand-primary)]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Team Settings</h2>
          <p className="text-sm text-muted-foreground">
            Assign offers and per-offer tabs for sales reps. Members never see All Offers.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setInvitePanelOpen(true)}
          className="rounded-xl bg-[var(--brand-primary)] px-4 py-2.5 text-sm font-medium text-white"
        >
          Invite Member
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-5">
          <p className="text-sm text-muted-foreground">Team Members</p>
          <p className="mt-2 text-3xl font-semibold">{users.length}</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <p className="text-sm text-muted-foreground">Sales Agents</p>
          <p className="mt-2 text-3xl font-semibold">{memberCount}</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <p className="text-sm text-muted-foreground">Pending Invites</p>
          <p className="mt-2 text-3xl font-semibold">{invites.length}</p>
        </div>
      </div>

      <div className="space-y-4">
        {users.map((user) => (
          <UserRow
            key={user._id}
            user={user}
            offers={offers}
            onSave={handleSaveUser}
            onDelete={handleDeleteUser}
          />
        ))}
      </div>

      {invites.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-5">
          <h3 className="text-lg font-semibold">Pending Invites</h3>
          <div className="mt-4 space-y-3">
            {invites.map((invite) => (
              <div key={invite._id} className="flex flex-col gap-2 rounded-xl border border-border p-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="font-medium">{invite.email}</p>
                  <p className="text-sm text-muted-foreground">{invite.invitedBy}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {invite.offerAccess.map((entry) => (
                    <PermissionChip key={`${invite._id}-${entry.offer}`} label={entry.offer} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <InvitePanel
        isOpen={invitePanelOpen}
        onClose={() => setInvitePanelOpen(false)}
        offers={offers}
        onInvite={handleInvite}
      />
    </div>
  );
}

const SETTINGS_TABS = [
  { id: "team", label: "Team" },
  { id: "appearance", label: "Appearance" },
] as const;

export function Settings() {
  const [activeTab, setActiveTab] = useState<"team" | "appearance">("team");

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex gap-1 rounded-xl bg-muted/50 p-1">
        {SETTINGS_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`rounded-lg px-5 py-2.5 text-sm font-medium transition-all ${
              activeTab === tab.id ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "team" ? <TeamTab /> : <ThemeSwitcher />}
    </div>
  );
}
